/**
 * This class constructs a Promise that can be resolved from the outside.
 * Use job.resolve/job.reject to resolve/reject job.promise
 * Use job.id to check if you are dealing with the correct job.
 */
class Job {
    promise;
    resolve;
    reject;
    id;
    constructor() {
        let { promise, reject, resolve } = this._createPromise();
        this.promise = promise;
        this.reject = reject;
        this.resolve = resolve;
        this.id = this._generateID();
    }
    /**
     * @returns A promise with external resolve/reject functions
     */
    _createPromise() {
        let promiseResolve;
        let promiseReject;
        const promise = new Promise((resolve, reject) => {
            promiseResolve = resolve;
            promiseReject = reject;
        });
        return {
            promise: promise,
            reject: promiseReject,
            resolve: promiseResolve,
        };
    }
    /**
    * Generate a unique id for a computing job.
    * Note : Can take up to 1 ms because of the methode used, if you need to generate a lot, change the method.
    */
    _generateID() {
        let u = new Date().getTime();
        while (u === Job._lastJobTime) {
            u = new Date().getTime();
        }
        Job._lastJobTime = u;
        return u;
    }
    static _lastJobTime = 0;
}

/**
 * Async service that sends computations to a worker and gives back results later.
 * Computations requests are managed asynchronously via a list of Job (Promise that can be resolved from the outside)
 *
 * *How to use*:
 *
```js
// myComputeService.js
export class ComputeService extends Service {
     _onWorkerMessage(e) {
          const job = this._popJob(e.data.processId);

        if (e.data.cmd == "customServiceCall") {
          job.resolve(e.data.result)
        }
     }

     customServiceCall(data) {
        return this._postMessage({cmd:"customServiceCall", data:data})
     }
}
```

```js
// myWorker.js
onmessage = (e) => {
    switch (e.data.cmd) {
        case 'customServiceCall': {

            const result = //do some computations

            postMessage({
                cmd: 'customServiceCall',
                result: result,
                processId: e.data.processId,
            });
            break;
        }
        default: {
            throw `Worker: command ${e.data.cmd} is unknown`;
        }
    }
}
```

```js
// main.js
const worker = new ModuleWorker();
await worker.load("worker/path/js")
const computeService = new ComputeService(worker)

const result = await computeService.customServiceCall(someData)
```
*/
class Service {
    worker;
    jobs = [];
    /**
     * Encapsulate the provided worker in a service.
     * @param  worker - worker to encapsulate
     */
    constructor(worker) {
        this.worker = worker;
        this.worker.onmessage = (e) => this._onWorkerMessage(e);
    }
    _popJob(processId) {
        let jobIndex = this.jobs.findIndex(job => job.id == processId);
        let job = this.jobs[jobIndex];
        this.jobs = this.jobs.filter((_, index) => index != jobIndex);
        return job;
    }
    _postMessage(e, transfer) {
        //create a new job and keep track of it
        let job = new Job();
        this.jobs.push(job);
        this.worker.postMessage({ ...e, processId: job.id }, transfer ?? []);
        // return the promise of the job's resolution
        return job.promise;
    }
    /**
     * Function executed when the worker sends a result back.
     *  @param e - Message from the worker
     *  @param e_data - serialized data
     *  @param e_data_cmd - command name
     *  @param e_data_processId - unique ID of the job
     */
    _onWorkerMessage(e) {
        // override this function
        let job = this._popJob(e.data.processId);
        job.resolve(e.data);
    }
}

/**
 * This class is made to replace Worker when it is not available (ie not in browser).
 * It will be importable but will fail when used.
 */
class BrokenWorkerClass {
    onmessage = null;
    onmessageerror = null;
    onerror = null;
    constructor(_scriptURL) {
        console.error("Workers from blob-workers cannot work in non browser enviroments where Worker is not defined. Try not to instantiate any worker.");
    }
    throwError() {
        throw new Error("blob-worker workers don't run in non-browser enviroments.");
    }
    postMessage() {
        this.throwError();
    }
    terminate() {
        this.throwError();
    }
    addEventListener() {
        this.throwError();
    }
    removeEventListener() {
        this.throwError();
    }
    dispatchEvent(event) {
        this.throwError();
        return false;
    }
}
let WorkerClass = typeof Worker !== 'undefined' ? Worker : BrokenWorkerClass;

/**
 * Integrates provided libs in the provided worker via a blobfile.
 * @deprecated use Service + ModuleWorker instead
 */
class BlobWorker extends WorkerClass {
    url;
    constructor(workerName, workerPath, workerLibPath) {
        if (typeof Worker === 'undefined') {
            throw new Error("Can't instantiate BlobWorker in non browser environement.");
        }
        let workerCode = `onmessage=(e)=> ${workerName}.onmessage(e, postMessage)`;
        let worker_string = `importScripts(${workerLibPath.map(libPath => `'${libPath}'`)}, '${workerPath}'); ${workerCode}`;
        let blob = new Blob([worker_string], { type: 'application/javascript' });
        let url = URL.createObjectURL(blob);
        super(url);
        this.url = url;
    }
    close() {
        this.terminate();
        URL.revokeObjectURL(this.url);
    }
}

/**
 * @public
 * Async service that sends computations to a worker and gives back results later.
 * It uses a BlobWorker internally.
 * @deprecated use Service + ModuleWorker instead
 */
class BlobService extends Service {
    /**
     * Creates the internal Worker which will perform the computations.
     * Computations requests are managed asynchronously via a list of Job (Promise that can be resolved from the outside)
     */
    constructor(workerName, workerPath, workerLibPaths) {
        super(new BlobWorker(workerName, workerPath, workerLibPaths));
    }
}

/**
 * Label that must be used in import map to indicate the worker loader location.
 */
const LOADER_PATH = "@dioxygen-software/blob-worker/dist/blob-worker.worker.js";
/**
 * Worker class that can load ESM code and perform import map resolution from the
 * current document's import map.
 *
 * *Note :* W3C standard provides an option to load ESM code in a worker, but it doesn't
 * perform import map resolution yet. So we need this custom class instead for now.
 *
 * *How it works:*
 * This class creates a worker with a default loading code on instanciation.
 * Then you must manually load into it your own ESM worker.
 *
 * *How to use:*
 * 1. create new instance : `const worker = new ModuleWorker()`
 * 2. load your ESM worker: `await worker.load('path/to/worker/code.js')`
 * 3. indicate the location of both your worker and the worker loader in your importmap :
 *     * worker loader label = "\@dioxygen-software/blob-worker/dist/blob-worker.worker.js"
 */
class ModuleWorker extends WorkerClass {
    #loaded = false;
    /**
     * Creates a default worker loader.
     */
    constructor() {
        if (typeof Worker === 'undefined') {
            throw new Error("Can't instantiate ModuleWorker in non browser environement.");
        }
        const loaderPath = getResolvedPath(LOADER_PATH);
        super(loaderPath);
        this.onerror = (ev) => {
            console.error("Worker error: ", ev);
        };
    }
    /**
     * Loads the provided ESM worker.
     * @param path - path to the ESM worker to load
     * @param options_resolve - indicates to resolve the provided path against the current document's import map (default = false)
     */
    async load(path, options) {
        if (this.#loaded)
            throw "[ModuleWorker] worker already loaded";
        this.#loaded = true;
        const resolvedPath = options?.resolve ? getResolvedPath(path) : path;
        const workerPath = new URL(resolvedPath, document.URL).href;
        const importMap = getImportMap();
        // compute absolute import maps (needed because shim will load from a blob)
        const imports = { ...importMap.imports };
        Object.keys(imports).forEach(key => {
            imports[key] = new URL(imports[key], document.URL).href;
        });
        return new Promise(resolve => {
            this.addEventListener("message", (e) => {
                if (e.data == "initDone")
                    resolve();
            });
            this.postMessage({ importMap: { imports: imports }, workerPath: workerPath });
        });
    }
}
/**
 * @param path path to resolve
 * @returns the resolved path according to the document's import map
 */
function getResolvedPath(path) {
    const importMap = getImportMap();
    const mapping = importMap.imports[path];
    if (!mapping)
        throw `[BlobWorker] Undefined import mapping for ${path}`;
    return mapping;
}
/**
 * @returns the import map of the current document
 */
function getImportMap() {
    const importMapElem = document.querySelector('script[type="importmap"]');
    if (!importMapElem)
        throw "[BlobWorker] import map is not defined";
    const importMapString = importMapElem.textContent;
    if (!importMapString || importMapString == "")
        throw "[BlobWorker] import map is empty";
    const importMap = JSON.parse(importMapString);
    return importMap;
}

export { BlobService, BlobWorker, Job, ModuleWorker, Service };
